'use client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ScreenSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl'
  | '8xl'
  | '9xl'
  | '10xl';
const screenSizes = [
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
  '7xl',
  '8xl',
  '9xl',
  '10xl',
];

type WindowContextValue = {
  scrollY: number;
  scrollX: number;
  isScrolling: boolean;
  isAtTop: boolean;
  innerWidth: number;
  innerHeight: number;
  outerWidth: number;
  outerHeight: number;
  screenWidth: number;
  screenHeight: number;
  screenX: number;
  screenY: number;
  orientation: string;
  devicePixelRatio: number;
  screenType: ScreenSize;
  screenFrom: (size: ScreenSize) => boolean;
  screenSmallerThan: (size: ScreenSize) => boolean;
  onScroll: (...args: any[]) => void;
  onScrollEnd: (...args: any[]) => void;
};

const WindowContext = createContext<WindowContextValue | undefined>(undefined);

export const WindowProvider = ({ children }: { children: React.ReactNode }) => {
  const [scrollY, setScrollY] = useState(0);
  const [scrollX, setScrollX] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [innerWidth, setInnerWidth] = useState(0);
  const [innerHeight, setInnerHeight] = useState(0);
  const [outerWidth, setOuterWidth] = useState(0);
  const [outerHeight, setOuterHeight] = useState(0);
  const [screenWidth, setScreenWidth] = useState(0);
  const [screenHeight, setScreenHeight] = useState(0);
  const [screenX, setScreenX] = useState(0);
  const [screenY, setScreenY] = useState(0);
  const [orientation, setOrientation] = useState<any>();
  const [devicePixelRatio, setDevicePixelRatio] = useState<any>();
  const [screenType, setScreenType] = useState<any>();
  const mediaSizes = useRef<any>({});
  const screenFrom = useCallback(
    (size: ScreenSize) => {
      return screenSizes.indexOf(size) <= screenSizes.indexOf(screenType);
    },
    [screenType]
  );
  const screenSmallerThan = useCallback(
    (size: ScreenSize) => {
      return screenSizes.indexOf(size) > screenSizes.indexOf(screenType);
    },
    [screenType]
  );

  const onScroll = useCallback((...args: any[]) => {
    setScrollY(window.scrollY);
    setScrollX(window.scrollX);
    setIsScrolling(true);
    setIsAtTop(window.scrollY === 0);
  }, []);

  const onScrollEnd = useCallback(() => {
    setIsScrolling(false);
  }, [setIsScrolling]);

  const onResize = useCallback(() => {
    setInnerWidth(window.innerWidth);
    setInnerHeight(window.innerHeight);
    setOuterWidth(window.outerWidth);
    setOuterHeight(window.outerHeight);
    setScreenWidth(window.screen.width);
    setScreenHeight(window.screen.height);
    setScreenX(window.screenX);
    setScreenY(window.screenY);
    setOrientation(window.screen.orientation.type);
    setDevicePixelRatio(window.devicePixelRatio);

    const width = window.innerWidth;
    const mediaType = Object.keys(mediaSizes.current).find(
      (key: string) => width >= mediaSizes.current[key as keyof typeof mediaSizes.current]
    );
    setScreenType(mediaType);
  }, []);

  useEffect(() => {
    const computedStyle = window.getComputedStyle(document.documentElement);
    mediaSizes.current = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']
      .reverse()
      .reduce((acc: any, size: string) => {
        const breakpoint = computedStyle.getPropertyValue(`--breakpoint-${size}`);

        return { ...acc, [size]: +breakpoint.replace('px', '') };
      }, {});

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    onResize();
    onScroll();
    onScrollEnd();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const value = useMemo<WindowContextValue>(
    () => ({
      scrollY,
      scrollX,
      isScrolling,
      isAtTop,
      onScroll,
      onScrollEnd,
      innerWidth,
      innerHeight,
      outerWidth,
      outerHeight,
      screenWidth,
      screenHeight,
      screenX,
      screenY,
      orientation,
      devicePixelRatio,
      screenType,
      screenFrom,
      screenSmallerThan,
    }),
    [
      scrollY,
      scrollX,
      isScrolling,
      isAtTop,
      onScroll,
      onScrollEnd,
      innerWidth,
      innerHeight,
      outerWidth,
      outerHeight,
      screenWidth,
      screenHeight,
      screenX,
      screenY,
      orientation,
      devicePixelRatio,
      screenType,
      screenFrom,
      screenSmallerThan,
    ]
  );

  return <WindowContext.Provider value={value}>{children}</WindowContext.Provider>;
};

export const useWindow = (): WindowContextValue => {
  const context = useContext(WindowContext);

  if (context === undefined) {
    throw new Error('useWindow must be used within a WindowProvider');
  }

  return context;
};
